import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import type { FunnelPeriod } from "../../../types/dealFunnel";
import type { Status } from "../../../types/deal";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { ErrorDialog } from "../../../components/common/errorDialog";
import { CustomMultiSelect } from "../../../components/common/inputFields/customMultiSelect";
import { apiGet } from "../../../services/genesisApi/httpsMethods";
import { formatDate } from "../../../utils/formatDate";
import { PeriodToggle } from "./PeriodToggle";
import { DealsByMonthLineChart } from "./DealsByMonthLineChart";

type DealLike = any;

const isAccordionLike = (deal: DealLike): boolean => {
  if (!deal || typeof deal !== "object") return false;
  return (
    typeof deal.deal_id === "number" ||
    typeof deal.accordion_id === "number" ||
    typeof deal.accordion_name === "string"
  );
};

const isClosedDeal = (
  deal: DealLike,
  selectedStatusIds: number[],
): boolean => {
  const status = deal?.status;
  const name =
    typeof status?.status === "string" ? status.status.trim().toLowerCase() : "";
  const hasClosedStatus =
    status?.is_closed === true ||
    name === "closed" ||
    name === "closed - apollo";

  if (!hasClosedStatus) return false;
  if (selectedStatusIds.length === 0) return true;
  return (
    typeof status?.id === "number" && selectedStatusIds.includes(status.id)
  );
};

const toValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    const isoDay = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (isoDay) {
      const local = new Date(
        Number(isoDay[1]),
        Number(isoDay[2]) - 1,
        Number(isoDay[3]),
      );
      return Number.isNaN(local.getTime()) ? null : local;
    }
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d;
};

function periodKey(date: Date, period: FunnelPeriod): string {
  const year = date.getFullYear();
  if (period === "year") return String(year);
  if (period === "quarter") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `${year}-Q${q}`;
  }
  return `${year}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function periodLabel(date: Date, period: FunnelPeriod): string {
  const year = date.getFullYear();
  if (period === "year") return String(year);
  if (period === "quarter") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `Q${q} ${year}`;
  }
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    year: "numeric",
  }).format(date);
}

function chartTitle(period: FunnelPeriod): string {
  if (period === "year") return "Closed Deals by Year";
  if (period === "quarter") return "Closed Deals by Quarter";
  return "Closed Deals by Month";
}

type ClosedBucket = {
  key: string;
  label: string;
  count: number;
  deals: DealLike[];
};

/** Inclusive empty buckets from earliest → latest (calendar order). */
function buildPeriodRange(
  minDate: Date,
  maxDate: Date,
  period: FunnelPeriod,
): ClosedBucket[] {
  const buckets: ClosedBucket[] = [];

  let y = minDate.getFullYear();
  let m = minDate.getMonth();
  if (period === "year") m = 0;
  else if (period === "quarter") m = Math.floor(m / 3) * 3;

  let endY = maxDate.getFullYear();
  let endM = maxDate.getMonth();
  if (period === "year") endM = 0;
  else if (period === "quarter") endM = Math.floor(endM / 3) * 3;

  // Ensure start <= end
  if (y > endY || (y === endY && m > endM)) {
    const ty = y;
    const tm = m;
    y = endY;
    m = endM;
    endY = ty;
    endM = tm;
  }

  while (y < endY || (y === endY && m <= endM)) {
    const cursor = new Date(y, m, 1);
    buckets.push({
      key: periodKey(cursor, period),
      label: periodLabel(cursor, period),
      count: 0,
      deals: [],
    });

    if (period === "year") {
      y += 1;
    } else if (period === "quarter") {
      m += 3;
      if (m > 11) {
        m = 0;
        y += 1;
      }
    } else {
      m += 1;
      if (m > 11) {
        m = 0;
        y += 1;
      }
    }
  }

  return buckets;
}

function dealName(deal: DealLike): string {
  const name = deal?.deal_name;
  if (typeof name === "string" && name.trim()) return name.trim();
  return typeof deal?.id === "number" ? `Deal ${deal.id}` : "Untitled deal";
}

const thSx = {
  textAlign: "left" as const,
  px: 2,
  py: 1.25,
  fontWeight: 600,
  borderBottom: "1px solid",
  borderColor: "divider",
};

const tdSx = {
  px: 2,
  py: 1,
  borderBottom: "1px solid",
  borderColor: "divider",
};

export function ClosedDealsTab() {
  const [isLoading, setIsLoading] = useState(false);
  const [period, setPeriod] = useState<FunnelPeriod>("quarter");
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const statusFilterInitialized = useRef(false);
  const [deals, setDeals] = useState<DealLike[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const { status: statusList } = useReferenceLists();
  const closedStatuses = useMemo(
    () =>
      ((statusList ?? []) as Status[])
        .filter((status) => status.is_closed === true)
        .sort((a, b) => a.sort_order - b.sort_order),
    [statusList],
  );
  const selectedStatusIds = useMemo(
    () => statusFilter.map((status) => status.id),
    [statusFilter],
  );

  useEffect(() => {
    if (closedStatuses.length > 0 && !statusFilterInitialized.current) {
      setStatusFilter(closedStatuses);
      statusFilterInitialized.current = true;
    }
  }, [closedStatuses]);

  useEffect(() => {
    setIsLoading(true);
    apiGet("deals?active_only=false")
      .then((response) => {
        if (response.success && response.data) {
          setDeals(response.data);
        } else {
          showErrorFromResponse(response);
        }
      })
      .catch(() => {
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch closed deals. Please try again.",
        });
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExpandedKeys(new Set());
  }, [period, selectedStatusIds]);

  const buckets = useMemo(() => {
    const map = new Map<string, ClosedBucket>();
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    for (const deal of deals) {
      if (isAccordionLike(deal)) continue;
      if (!isClosedDeal(deal, selectedStatusIds)) continue;

      const closing = toValidDate(deal?.target_closing);
      if (!closing) continue;

      if (!minDate || closing < minDate) minDate = closing;
      if (!maxDate || closing > maxDate) maxDate = closing;

      const key = periodKey(closing, period);
      const label = periodLabel(closing, period);
      const existing = map.get(key);
      if (existing) {
        existing.deals.push(deal);
        existing.count += 1;
      } else {
        map.set(key, { key, label, count: 1, deals: [deal] });
      }
    }

    if (!minDate || !maxDate) return [];

    const filledBuckets = buildPeriodRange(minDate, maxDate, period).map(
      (empty) => {
        const filled = map.get(empty.key);
        if (!filled) return empty;
        return {
          ...filled,
          deals: [...filled.deals].sort((a, bDeal) => {
            const aT = toValidDate(a?.target_closing)?.getTime() ?? 0;
            const bT = toValidDate(bDeal?.target_closing)?.getTime() ?? 0;
            return aT - bT;
          }),
        };
      },
    );

    // Explicit ascending by period key (earliest first)
    return filledBuckets.sort((a, b) => a.key.localeCompare(b.key));
  }, [deals, period, selectedStatusIds]);

  const chartPoints = useMemo(
    () => buckets.map((b) => ({ key: b.key, label: b.label, count: b.count })),
    [buckets],
  );

  const totalClosed = useMemo(
    () => buckets.reduce((sum, b) => sum + b.count, 0),
    [buckets],
  );

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: "20px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          Loading closed deals…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#182737" }}>
            {totalClosed.toLocaleString("en-GB")} closed deal
            {totalClosed === 1 ? "" : "s"} with a closing date
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CustomMultiSelect
              value={statusFilter.map((status) => status.id)}
              onChange={(e) => {
                const selectedIds = e.target.value as (string | number)[];
                const selectedStatuses = closedStatuses.filter((opt) =>
                  selectedIds.includes(opt.id),
                );
                setStatusFilter(selectedStatuses);
              }}
              options={closedStatuses}
              placeholder="Select closed status"
              labelKey="status"
              minWidth="200px"
            />
            <PeriodToggle
              value={period}
              onChange={(next) => {
                setPeriod(next);
                setExpandedKeys(new Set());
              }}
            />
          </Box>
        </Box>

        {buckets.length === 0 ? (
          <Typography sx={{ fontSize: "14px", color: "#182737", py: 2 }}>
            No closed deals with a closing date found.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <DealsByMonthLineChart
              points={chartPoints}
              title={chartTitle(period)}
            />

            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                  color: "#182737",
                }}
              >
                <Box component="thead" sx={{ backgroundColor: "#f5f7f9" }}>
                  <Box component="tr">
                    <Box component="th" sx={{ ...thSx, width: 44 }} />
                    <Box component="th" sx={thSx}>
                      Period
                    </Box>
                    <Box component="th" sx={{ ...thSx, textAlign: "right" }}>
                      Closed deals
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {buckets.map((b) => {
                    const open = expandedKeys.has(b.key);
                    const canExpand = b.count > 0;
                    return (
                      <Fragment key={b.key}>
                        <Box
                          component="tr"
                          onClick={() => {
                            if (canExpand) toggleExpanded(b.key);
                          }}
                          sx={{
                            cursor: canExpand ? "pointer" : "default",
                            color: b.count === 0 ? "text.secondary" : "#182737",
                            "&:hover": canExpand
                              ? { backgroundColor: "action.hover" }
                              : undefined,
                          }}
                        >
                          <Box component="td" sx={{ ...tdSx, width: 44, pr: 0 }}>
                            {canExpand ? (
                              <IconButton
                                size="small"
                                aria-label={open ? "Collapse" : "Expand"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpanded(b.key);
                                }}
                              >
                                {open ? (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : null}
                          </Box>
                          <Box component="td" sx={tdSx}>
                            {b.label}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              ...tdSx,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {b.count.toLocaleString("en-GB")}
                          </Box>
                        </Box>
                        {canExpand ? (
                          <Box component="tr">
                            <Box
                              component="td"
                              colSpan={3}
                              sx={{
                                p: 0,
                                borderBottom: open ? 1 : 0,
                                borderColor: "divider",
                              }}
                            >
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    pl: 7,
                                    backgroundColor: "#fafbfc",
                                  }}
                                >
                                  {b.deals.map((deal) => (
                                    <Box
                                      key={deal.id ?? dealName(deal)}
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "baseline",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        py: 0.75,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        "&:last-of-type": { borderBottom: 0 },
                                      }}
                                    >
                                      {typeof deal.id === "number" ? (
                                        <Box
                                          component="a"
                                          href={`/deals/${deal.id}`}
                                          sx={{
                                            color: "#1B7A63",
                                            textDecoration: "underline",
                                            fontWeight: 600,
                                            fontSize: "13px",
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {dealName(deal)}
                                        </Box>
                                      ) : (
                                        <Typography
                                          sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {dealName(deal)}
                                        </Typography>
                                      )}
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          color: "text.secondary",
                                        }}
                                      >
                                        Closing:{" "}
                                        {formatDate(deal.target_closing)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Collapse>
                            </Box>
                          </Box>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
